import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Banner from '../assets/images/banner.png';
import BannerVajpayee from '../assets/images/banner_vajpayee.png';
import BannerModi from '../assets/images/banner_modi.png';
import { Crown, Scale, HandHelping, Users, AlertCircle, Clock, ArrowRight, Download, Eye, X, BookOpen, Calendar, MapPin, Building2, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import worksData from '../data/works.json';
import electionManifestoData from '../data/election_manifesto.json';
import eventsData from '../data/events.json';
import { useAuth } from '../context/AuthContext';

// Animated Counter Component for scroll-triggered count up
function AnimatedCounter({ end, duration = 900 }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const node = counterRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTime = null;
          const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            // Easing function (easeOutQuad) for smooth count animation
            const easeOutQuad = 1 - (1 - progress) * (1 - progress);
            setCount(Math.floor(easeOutQuad * end));
            if (progress < 1) {
              window.requestAnimationFrame(step);
            } else {
              setCount(end);
            }
          };
          window.requestAnimationFrame(step);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return <span ref={counterRef}>{count}</span>;
}

function Main() {
  const { isAuthenticated } = useAuth();

  // Banner Carousel State
  const banners = [
    { id: 1, src: Banner, title: "பாரதிய ஜனதா கட்சி" },
    { id: 2, src: BannerModi, title: "பிரதமர் மோடி சிறந்த திட்டங்கள்" },
    { id: 3, src: BannerVajpayee, title: "அடல் பிஹாரி வாஜ்பாய் சிறந்த திட்டங்கள்" }
  ];
  const [currentBanner, setCurrentBanner] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 2500);
    return () => clearInterval(timer);
  }, [isPaused, banners.length]);

  const nextBanner = () => setCurrentBanner((prev) => (prev + 1) % banners.length);
  const prevBanner = () => setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);

  // Function to determine work status
  const getWorkStatus = (startDate, endDate) => {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (today >= start && today <= end) {
      return 'current';
    } else if (today < start) {
      return 'upcoming';
    } else {
      return 'completed';
    }
  };

  // Filter and sort works
  const currentWorks = worksData.filter(work => getWorkStatus(work.start_date, work.end_date) === 'current');
  const upcomingWorks = worksData.filter(work => getWorkStatus(work.start_date, work.end_date) === 'upcoming')
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

  const displayWorks = [...currentWorks, ...upcomingWorks];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ta-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Manifesto
  const [selectedManifesto, setSelectedManifesto] = useState(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);

  const handleViewPDF = (manifesto) => {
    setSelectedManifesto(manifesto);
    setShowPDFViewer(true);
  };

  const handleDownload = (manifesto) => {
    const link = document.createElement('a');
    link.href = `/assets/election_manifesto/${manifesto.file}`;
    link.download = manifesto.file;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closePDFViewer = () => {
    setShowPDFViewer(false);
    setSelectedManifesto(null);
  };


  return (
    <div>
      {/* Hero Banner Carousel */}
      <div 
        className="relative overflow-hidden group bg-gradient-to-b from-gray-900 via-gray-900 to-orange-950 shadow-lg"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="relative w-full h-auto max-h-[580px] overflow-hidden flex items-center justify-center min-h-[220px] sm:min-h-[350px] md:min-h-[480px]">
          {banners.map((b, index) => (
            <div
              key={b.id}
              className={`w-full transition-all duration-300 ease-in-out ${
                index === currentBanner ? 'opacity-100 relative z-10 scale-100' : 'opacity-0 absolute inset-0 z-0 scale-95 pointer-events-none'
              }`}
            >
              <img
                src={b.src}
                alt={b.title}
                className="w-full h-auto max-h-[580px] object-contain mx-auto block"
              />
            </div>
          ))}
        </div>

        {/* Carousel Navigation Buttons */}
        <button
          onClick={prevBanner}
          className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-20 p-2.5 sm:p-3 rounded-full bg-black/40 hover:bg-orange-600 text-white backdrop-blur-md transition-all duration-200 opacity-80 hover:opacity-100 shadow-xl border border-white/10"
          aria-label="Previous Banner"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        <button
          onClick={nextBanner}
          className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-20 p-2.5 sm:p-3 rounded-full bg-black/40 hover:bg-orange-600 text-white backdrop-blur-md transition-all duration-200 opacity-80 hover:opacity-100 shadow-xl border border-white/10"
          aria-label="Next Banner"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      {/* BJP Ruled States Section */}
      <div className="bg-gradient-to-br from-orange-50 to-white py-8 sm:py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4 leading-snug">
              தேசிய ஜனநாயக கூடஂடணி ஆட்சியில் உள்ள மாநிலங்கள்
            </h2>
            <p className="text-sm sm:text-lg md:text-xl text-gray-600">
              2026 நிலவரம்
            </p>
            <div className="w-16 sm:w-24 h-1 bg-orange-500 mx-auto mt-3 sm:mt-4"></div>
          </div>

          {/* BJP Direct Rule States */}
          <div className="mb-10 sm:mb-12">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-orange-600 mb-4 sm:mb-6 text-center">
              BJP நேரடி ஆட்சி
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {[
                'உத்தரப் பிரதேசம்',
                'மத்தியப் பிரதேசம்',
                'குஜராத்',
                'ராஜஸ்தான்',
                'சத்தீஸ்கர்',
                'ஒடிசா',
                'அசாம்',
                'ஹரியானா',
                'உத்தரகாண்ட்',
                'கோவா',
                'அருணாச்சலப் பிரதேசம்',
                'திரிபுரா',
                'மேற்கு வங்காளம்'
              ].map((state, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg sm:rounded-xl shadow-sm sm:shadow-md hover:shadow-lg transition-all duration-300 p-3 sm:p-5 border-l-4 border-orange-500"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-orange-500 rounded-full animate-pulse"></div>
                    <p className="font-medium sm:font-semibold text-sm sm:text-base text-gray-800 leading-snug">
                      {state}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* NDA Coalition States */}
          <div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600 mb-4 sm:mb-6 text-center">
              NDA கூட்டணி ஆட்சி
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {[
                { state: 'மகாராஷ்டிரா', coalition: 'BJP + கூட்டணி' },
                { state: 'பீகார்', coalition: 'BJP + கூட்டணி' },
                { state: 'ஆந்திரப் பிரதேசம்', coalition: 'BJP + கூட்டணி' },
                { state: 'நாகாலாந்து', coalition: 'NDPP + BJP' },
                { state: 'மேகாலயா', coalition: 'BJP + கூட்டணி' },
                { state: 'சிக்கிம்', coalition: 'கூட்டணி ஆதரவு' },
                { state: 'புதுச்சேரி (யூனியன் பிரதேசம்)', coalition: 'கூட்டணி ஆதரவு' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg sm:rounded-xl shadow-sm sm:shadow-md hover:shadow-lg transition-all duration-300 p-3 sm:p-5 border-l-4 border-blue-500"
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full mt-1 animate-pulse"></div>
                    <div>
                      <p className="font-medium sm:font-semibold text-sm sm:text-base text-gray-800">
                        {item.state}
                      </p>
                      <p className="text-[11px] sm:text-xs text-gray-600 leading-tight">
                        {item.coalition}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary Box with Scroll-Triggered Animated Counters */}
          <div className="mt-10 sm:mt-12 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 rounded-2xl shadow-2xl p-6 sm:p-10 text-white text-center border border-orange-400/30">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              
              <div className="bg-white/10 backdrop-blur-md p-4 sm:p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1 shadow-lg">
                <div className="text-4xl sm:text-6xl font-black mb-1 sm:mb-2 tracking-tight text-white drop-shadow-lg">
                  <AnimatedCounter end={12} duration={2000} />
                </div>
                <div className="text-sm sm:text-base font-bold text-orange-100 uppercase tracking-wider">
                  BJP நேரடி ஆட்சி
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-4 sm:p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1 shadow-lg">
                <div className="text-4xl sm:text-6xl font-black mb-1 sm:mb-2 tracking-tight text-white drop-shadow-lg">
                  <AnimatedCounter end={7} duration={2000} />
                </div>
                <div className="text-sm sm:text-base font-bold text-orange-100 uppercase tracking-wider">
                  NDA கூட்டணி ஆட்சி
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-4 sm:p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1 shadow-lg">
                <div className="text-4xl sm:text-6xl font-black mb-1 sm:mb-2 tracking-tight text-white drop-shadow-lg">
                  <AnimatedCounter end={19} duration={2000} />
                </div>
                <div className="text-sm sm:text-base font-bold text-orange-100 uppercase tracking-wider">
                  மொத்த மாநிலங்கள்
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Main Content Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Our Principles Section start*/}
        <div className="mb-10">
          <div className="text-center mb-6 md:mb-12 px-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-2 md:mb-4 leading-snug">
              நமது கொள்கைகள்
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-orange-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Social Justice Card */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-orange-600 opacity-90"></div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
              
              <div className="relative p-8 md:p-12 flex flex-col items-center justify-center min-h-[300px] text-center">
                <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Scale className="w-10 h-10" />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  சமூக நீதி
                </h3>
                <p className="text-white text-lg opacity-90 leading-relaxed">
                  அனைவருக்கும் சம உரிமைகள், சம வாய்ப்புகள், சம மரியாதை மற்றும் ஒருங்கிணைந்த வளர்ச்சி.
                </p>
              </div>
            </div>

            {/* Democracy Protection Card */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-green-600 to-green-700 opacity-90"></div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
              
              <div className="relative p-8 md:p-12 flex flex-col items-center justify-center min-h-[300px] text-center">
                <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Crown className="w-10 h-10" />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  ஜனநாயகம் காப்போம்
                </h3>
                <p className="text-white text-lg opacity-90 leading-relaxed">
                  மக்களாட்சி உரிமைகளை பாதுகாத்தல், வெளிப்படைத்தன்மை மற்றும் பொறுப்புணர்வு.
                </p>
              </div>
            </div>

            {/* Welfare of Poor & Underprivileged Card */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-700 opacity-90"></div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>

              <div className="relative p-8 md:p-12 flex flex-col items-center justify-center min-h-[300px] text-center">
                <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <HandHelping className="w-10 h-10" />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  சமூக நலன்
                </h3>
                <p className="text-white text-lg opacity-90 leading-relaxed">
                  ஏழை எளிய மக்களின் வாழ்க்கை மேம்படுத்துதல், அடிப்படை வசதிகள் மற்றும் சமூக பாதுகாப்பு.
                </p>
              </div>
            </div>

            {/* National Unity Card */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-red-700 opacity-90"></div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>

              <div className="relative p-8 md:p-12 flex flex-col items-center justify-center min-h-[300px] text-center">
                <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-10 h-10" />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  தேசிய ஒருமைப்பாடு
                </h3>
                <p className="text-white text-lg opacity-90 leading-relaxed">
                  நாட்டின் ஒற்றுமை மற்றும் ஒருமித்த தன்மையை காப்போம்.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Our Principles section end */}

        {/* Recent Events Section */}
        <div className="mb-14">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8 px-4 gap-4">
            <div className="text-center sm:text-left">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 font-semibold text-xs rounded-full mb-2">
                <Sparkles className="w-3.5 h-3.5" />
                கட்சி செயல்பாடுகள்
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-800 leading-snug">
                சமீபத்திய நிகழ்வுகள்
              </h2>
              <div className="w-16 h-1 bg-orange-500 mt-2 mx-auto sm:mx-0"></div>
            </div>
            
            <Link
              to="/events"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm"
            >
              <span>அனைத்து நிகழ்வுகள் பார்க்க</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventsData.slice(0, 3).map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col group"
              >
                <div className="relative h-52 overflow-hidden bg-gray-100">
                  <img
                    src={`/assets/events/${event.banner_image || event.images[0]}`}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&fit=crop';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  
                  <div className="absolute top-3 left-3">
                    <span className="bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-lg shadow flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {event.date_display}
                    </span>
                  </div>

                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <h3 className="text-lg font-bold leading-snug drop-shadow">
                      {event.title}
                    </h3>
                  </div>
                </div>

                <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-2 text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                      <span className="font-semibold text-gray-900">{event.constituency}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-600">{event.location}</span>
                    </div>
                  </div>

                  <Link
                    to="/events"
                    className="w-full py-2 px-4 bg-orange-50 hover:bg-orange-100 text-orange-700 font-semibold rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
                  >
                    <span>முழு விபரம்</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ongoing Works Section - Protected */}
        {isAuthenticated ? (
          <div className="mb-10">
            <div className="text-center mb-6 md:mb-12 px-2">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-2 md:mb-4 leading-snug">
                நடைபெறும் பணிகள்
              </h2>
              <div className="w-16 sm:w-20 md:w-24 h-1 bg-orange-500 mx-auto"></div>
            </div>

            {/* Authenticated Content - Show Works */}
            {displayWorks.length > 0 ? (
              <div className="relative">
                {/* Horizontal Scroll Container */}
                <div
                  className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide"
                  style={{ scrollBehavior: "smooth" }}
                >
                  {displayWorks.map((work) => {
                    const status = getWorkStatus(work.start_date, work.end_date);
                    const isCurrent = status === "current";

                    return (
                      <div
                        key={work.id}
                        className={`flex-none w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] snap-start ${
                          isCurrent ? "ring-2 ring-orange-500" : ""
                        }`}
                      >
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 h-full">
                          
                          {/* Banner Image */}
                          <div className="relative h-56 overflow-hidden">
                            <img
                              src={`/assets/works/${work.work_banner_image}`}
                              alt={work.work_name}
                              className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-300"
                              onError={(e) => {
                                e.target.src =
                                  "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&h=400&fit=crop";
                              }}
                            />

                            {/* Status Badge */}
                            <div className="absolute top-4 right-4">
                              <span
                                className={`px-4 py-2 rounded-full font-semibold text-sm shadow-lg flex items-center gap-2 ${
                                  isCurrent
                                    ? "bg-orange-500 text-white"
                                    : "bg-blue-500 text-white"
                                }`}
                              >
                                {isCurrent && (
                                  <AlertCircle className="w-4 h-4 animate-pulse" />
                                )}
                                {isCurrent ? "நடைபெறுகிறது" : "வரவிருக்கிறது"}
                              </span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                              {work.work_name}
                            </h3>

                            <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                              {work.work_description}
                            </p>

                            {/* Date Info */}
                            <div className="space-y-2 bg-gray-50 p-4 rounded-lg mb-4">
                              <div className="flex items-start text-sm text-gray-700">
                                <Clock className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold">தொடக்கம்: </span>
                                  <span>{formatDate(work.start_date)}</span>
                                </div>
                              </div>
                              <div className="flex items-start text-sm text-gray-700">
                                <Clock className="w-4 h-4 mr-2 mt-0.5 text-red-600 flex-shrink-0" />
                                <div>
                                  <span className="font-semibold">முடிவு: </span>
                                  <span>{formatDate(work.end_date)}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Scroll Hint */}
                {displayWorks.length > 3 && (
                  <div className="text-center mt-4">
                    <div className="inline-flex items-center gap-2 text-gray-500 text-sm">
                      <ArrowRight className="w-4 h-4" />
                      <span>Scroll to view more works</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-lg shadow-md">
                <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  பணிகள் இல்லை
                </h3>
                <p className="text-gray-500">
                  தற்போது நடைபெறும் பணிகள் எதுவும் இல்லை
                </p>
              </div>
            )}
          </div>
        ) : (
          <p></p>
        )}
        {/* Ongoing Works Section end*/}  

        {/* Manifesto Section - Start */}
        {isAuthenticated ? (
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="mb-10">
              <div className="text-center mb-6 md:mb-12 px-4">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-2 md:mb-4 leading-snug">
                  தேர்தல் அறிக்கைகளஂ
                </h2>
                <div className="w-16 sm:w-20 md:w-24 h-1 bg-orange-500 mx-auto"></div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto lg:px-4 sm:px-6 lg:px-8 py-8">
            

              {/* Manifestos Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                {electionManifestoData.map((manifesto) => (
                  <div
                    key={manifesto.id}
                    className="group relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border-2 border-orange-100 hover:border-orange-300 transform hover:-translate-y-2"
                  >
                    {/* Decorative Top */}
                    <div className="relative h-32 bg-gradient-to-br from-orange-500 to-orange-600 overflow-hidden">
                      <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -mr-20 -mt-20"></div>
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full -ml-16 -mb-16"></div>
                      </div>
                      
                      <div className="relative h-full flex items-center justify-center">
                        <div className="w-20 h-20 bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
                          <BookOpen className="w-10 h-10 text-white" />
                        </div>
                      </div>

                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {/* Election Name */}
                      <div className="mb-6">
                        <div className="flex items-start gap-3 mb-3">
                          <Calendar className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                          <h3 className="text-xl font-bold text-gray-800 leading-tight">
                            {manifesto.election_name}
                          </h3>
                        </div>
                      </div>


                      {/* Action Buttons */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          onClick={() => handleViewPDF(manifesto)}
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg"
                        >
                          <Eye className="w-4 h-4" />
                          <span>பார்க்க</span>
                        </button>
                        <button
                          onClick={() => handleDownload(manifesto)}
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-md hover:shadow-lg"
                        >
                          <Download className="w-4 h-4" />
                          <span>பதிவிறக்க</span>
                        </button>
                      </div>
                    </div>

                    {/* Hover Glow Effect */}
                    <div className="absolute inset-0 border-2 border-orange-400 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                ))}
              </div>

              {/* No Results */}
              {electionManifestoData.length === 0 && (
                <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
                  <BookOpen className="w-20 h-20 text-gray-300 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-gray-600 mb-3">
                    தேர்தல் அறிக்கைகள் இல்லை
                  </h3>
                  <p className="text-gray-500 text-lg">
                    தற்போது தேர்தல் அறிக்கைகள் எதுவும் கிடைக்கவில்லை
                  </p>
                </div>
              )}
            </div>

            {/* PDF Viewer Modal */}
            {showPDFViewer && selectedManifesto && (
              <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-2xl">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <BookOpen className="w-7 h-7" />
                        <h2 className="text-2xl font-bold">{selectedManifesto.election_name}</h2>
                      </div>
                      
                    </div>
                    <button
                      onClick={closePDFViewer}
                      className="p-3 hover:bg-white hover:bg-opacity-20 rounded-xl transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  {/* PDF Viewer */}
                  <div className="flex-1 overflow-hidden bg-gray-100">
                    <iframe
                      src={`/assets/election_manifesto/${selectedManifesto.file}`}
                      className="w-full h-full border-0"
                      title={selectedManifesto.election_name}
                    />
                  </div>

                  {/* Modal Footer */}
                  <div className="p-4 border-t bg-gray-50 rounded-b-2xl flex justify-between items-center">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4 text-orange-500" />
                      <span className="font-medium">{selectedManifesto.election_name}</span>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleDownload(selectedManifesto)}
                        className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-colors flex items-center gap-2 shadow-md"
                      >
                        <Download className="w-4 h-4" />
                        பதிவிறக்க
                      </button>
                      <button
                        onClick={closePDFViewer}
                        className="px-6 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                      >
                        மூடு
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}


        </div>   
        ) : (
          <p></p>
        )} 
        {/* Manifesto Section - End */}       

      </div>
      {/* Main Content End */}
    </div>
  );
}

export default Main;